import { Check, Loader2, MailOpen, X } from 'lucide-react';
import type { WorkspaceInvitation } from '@/types';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAcceptInvitation, useDeclineInvitation, useFetchMyInvitations } from '@/hooksApi';
import WorkspaceTile from './WorkspaceTile';

const CARD = 'rounded-2xl border border-sd-border bg-sd-card shadow-sm';

/**
 * BOXY-44 P5.2 — the invitee's side: workspaces you have been asked to join.
 *
 * Built in the shadcn layer (`@/components/ui`, `sd-*` tokens, lucide, `toast`)
 * rather than Ant Design. The migration layer's own header says Ant Design is
 * being REMOVED, so a brand-new surface written against it is debt created on
 * purpose — and a settings page that does not match the one beside it teaches
 * the merchant that this part of the product is somewhere else.
 *
 * The server only ever returns OPEN invitations (pending and not expired), so
 * there is no client-side filtering here and there must not be: an expired or
 * revoked invitation is not an action anyone can take, and rendering one
 * produces a badge the user cannot clear.
 *
 * Accepting joins the workspace; it does NOT switch into it. Switching swaps
 * the token and clears the cache, which would pull the ground out from under
 * whatever the user had open — two different intents, two different clicks.
 */
/**
 * How an invitation's roles are written.
 *
 * A role the merchant created gets THEIR name for it, verbatim — renaming
 * `finance` to something else is a decision they made and the UI must not
 * overrule it. Only our own shared preset gets a translated label, because
 * "admin" is a word we chose rather than a name they did.
 *
 * An EMPTY set means an invitation sent before access roles existed. The server
 * refuses to accept those, so it is named as needing a re-send rather than
 * given a plausible-looking label that would only mislead.
 */
const invitationRoleLabel = (
  roles: WorkspaceInvitation['roles'],
  t: (key: string) => unknown,
): string => {
  if (!roles || roles.length === 0) return String(t('workspace.role_unavailable'));

  return roles
    .map((role) => (role.is_default ? String(t('workspace.role_admin')) : role.name))
    .join(', ');
};

const InvitationsPanel = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useFetchMyInvitations();
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();

  const invitations = data?.object ?? [];

  // A skeleton, not a spinner and not an empty state: "you have no invitations"
  // is a different statement from "not known yet", and showing the first one
  // while the second is true is how a merchant concludes the feature is broken.
  if (isLoading) {
    return (
      <div className={`${CARD} p-6`}>
        <div className='animate-pulse space-y-4'>
          <div className='h-4 w-1/3 rounded bg-sd-muted' />
          <div className='h-4 w-1/2 rounded bg-sd-muted' />
        </div>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className={`${CARD} flex flex-col items-center gap-3 px-6 py-12 text-center`}>
        <div className='flex h-12 w-12 items-center justify-center rounded-full bg-sd-muted'>
          <MailOpen className='h-5 w-5 text-sd-muted-foreground' aria-hidden />
        </div>
        <p className='text-sm font-medium text-sd-foreground'>
          {String(t('workspace.no_invitations'))}
        </p>
        {/* An empty state that only says "empty" leaves the merchant wondering
            whether they are looking at the wrong screen. This says how one
            would arrive. */}
        <p className='max-w-sm text-[13px] leading-relaxed text-sd-muted-foreground'>
          {String(t('workspace.no_invitations_hint'))}
        </p>
      </div>
    );
  }

  const onAccept = async (uid: string) => {
    try {
      await accept.mutateAsync(uid);
      toast({ title: String(t('workspace.accepted')) });
    } catch (error: any) {
      /*
       * The server's own sentence when it has one. Accepting can be refused for
       * reasons the invitee can act on — the invitation is no longer pending,
       * or it predates workspace roles and has to be sent again — and a bare
       * "Error" hides the only thing that tells them what to do.
       *
       * ⚠️ From `response.data.message`, NOT from `error.message`. These come
       * back as a real HTTP 400, so the Error an axios rejection carries reads
       * "Request failed with status code 400" — which is worse than the generic
       * string it would be replacing.
       */
      const message = error?.response?.data?.message;
      toast({ title: message || String(t('error')), variant: 'destructive' });
    }
  };

  const onDecline = async (uid: string) => {
    try {
      await decline.mutateAsync(uid);
      toast({ title: String(t('workspace.declined')) });
    } catch {
      toast({ title: String(t('error')), variant: 'destructive' });
    }
  };

  const busy = accept.isLoading || decline.isLoading;

  return (
    <ul className='list-none space-y-3 p-0' aria-label={String(t('workspace.invitations'))}>
      {invitations.map((invitation) => (
        <li
          key={invitation.uid}
          className={`${CARD} flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between`}
        >
          <div className='flex min-w-0 items-center gap-3'>
            {/* The same tile the workspace has everywhere else — a generic
                building icon makes every invitation look identical, which is
                the one thing a list of invitations must not do. */}
            <WorkspaceTile
              uid={invitation.workspace?.uid ?? invitation.uid}
              name={invitation.workspace?.name}
              className='h-10 w-10 rounded-xl text-sm'
            />
            <div className='min-w-0'>
              {/* The workspace name is the decision. It leads, at the weight of
                  a heading, and truncates rather than wrapping the row. */}
              <bdi dir='auto' className='block truncate text-sm font-semibold text-sd-foreground'>
                {invitation.workspace?.name ?? String(t('workspace.untitled'))}
              </bdi>
              <p className='flex items-center gap-1.5 truncate text-[13px] text-sd-muted-foreground'>
                {String(t('workspace.invited_you'))}
                {/* "Admin", not "ADMIN" — the same chip the switcher and the
                    page use, so one role reads as one thing everywhere. */}
                <bdi
                  dir='auto'
                  className='shrink-0 rounded-full border border-sd-border px-1.5 py-px text-[11px] text-sd-foreground'
                >
                  {invitationRoleLabel(invitation.roles, t)}
                </bdi>
              </p>
            </div>
          </div>

          {/* Accept is the primary and comes first; decline is quiet and
              destructive-on-hover only. Both are full width on a phone, where a
              row of two small buttons is the hardest thing to hit. */}
          <div className='flex shrink-0 gap-2'>
            <Button
              size='sm'
              className='flex-1 sm:flex-none'
              disabled={busy}
              onClick={() => onAccept(invitation.uid)}
            >
              {accept.isLoading ? (
                <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
              ) : (
                <Check className='h-4 w-4' aria-hidden />
              )}
              {String(t('workspace.accept'))}
            </Button>
            <Button
              size='sm'
              variant='outline'
              className='flex-1 sm:flex-none'
              disabled={busy}
              onClick={() => onDecline(invitation.uid)}
            >
              <X className='h-4 w-4' aria-hidden />
              {String(t('workspace.decline'))}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default InvitationsPanel;
